import { useMemo, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ToggleSwitch } from '@/components/settings/ToggleSwitch';
import { hasPurchasePriceForMargin, purchasePriceNumericOrNull } from '@/lib/margin';
import { cn } from '@/lib/utils';
import { MarginBadge, getRagStatus } from './MarginBadge';

export interface MarginCalculatorProps {
  purchasePrice: number;
  sellPrice: number;
  venueCommissionPercent?: number;
  targetMarginPercent?: number;
  supplierName?: string;
  className?: string;
}

export interface MarginResult {
  commissionAmount: number;
  netRevenue: number;
  grossProfit: number;
  grossMarginPercent: number;
  operatingMarginPercent: number;
  meetsTarget: boolean;
  suggestedSellPrice: number | null;
}

export function calculateMarginResult(
  purchasePrice: number,
  sellPrice: number,
  venueCommissionPercent: number,
  targetMarginPercent: number,
  includeVenueCommission = false
): MarginResult | null {
  if (!(purchasePrice > 0) || !Number.isFinite(purchasePrice)) {
    return null;
  }
  if (sellPrice <= 0) {
    return {
      commissionAmount: 0,
      netRevenue: 0,
      grossProfit: 0,
      grossMarginPercent: 0,
      operatingMarginPercent: 0,
      meetsTarget: false,
      suggestedSellPrice: null,
    };
  }

  const effectiveCommissionPercent = includeVenueCommission ? venueCommissionPercent : 0;
  const commissionAmount = sellPrice * (effectiveCommissionPercent / 100);
  const netRevenue = sellPrice - commissionAmount;
  const grossProfit = netRevenue - purchasePrice;
  const grossMarginPercent = ((sellPrice - purchasePrice) / sellPrice) * 100;
  const operatingMarginPercent = (grossProfit / sellPrice) * 100;
  const marginForTarget = includeVenueCommission ? operatingMarginPercent : grossMarginPercent;
  const meetsTarget = marginForTarget >= targetMarginPercent;

  const denominator = includeVenueCommission
    ? 1 - venueCommissionPercent / 100 - targetMarginPercent / 100
    : 1 - targetMarginPercent / 100;
  const suggestedSellPrice =
    denominator > 0 ? Math.ceil((purchasePrice / denominator) * 100) / 100 : null;

  return {
    commissionAmount: Math.round(commissionAmount * 100) / 100,
    netRevenue: Math.round(netRevenue * 100) / 100,
    grossProfit: Math.round(grossProfit * 100) / 100,
    grossMarginPercent: Math.round(grossMarginPercent * 100) / 100,
    operatingMarginPercent: Math.round(operatingMarginPercent * 100) / 100,
    meetsTarget,
    suggestedSellPrice,
  };
}

function formatCurrency(value: number): string {
  return `£${value.toFixed(2)}`;
}

export function MarginCalculator({
  purchasePrice,
  sellPrice,
  venueCommissionPercent = 20,
  targetMarginPercent = 60,
  supplierName,
  className,
}: MarginCalculatorProps) {
  const [includeVenueCommission, setIncludeVenueCommission] = useState(false);
  const hasPurchaseCost = hasPurchasePriceForMargin(purchasePrice);

  const result = useMemo(() => {
    const purchaseNum = purchasePriceNumericOrNull(purchasePrice);
    if (purchaseNum == null) return null;
    return calculateMarginResult(
      purchaseNum,
      sellPrice,
      venueCommissionPercent,
      targetMarginPercent,
      includeVenueCommission
    );
  }, [purchasePrice, sellPrice, venueCommissionPercent, targetMarginPercent, includeVenueCommission]);

  const ragStatus = result ? getRagStatus(result.grossMarginPercent) : 'red';

  return (
    <Card className={cn('', className)}>
      <CardHeader className="pb-2">
        <CardTitle className="text-base">Margin Calculator</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="grid grid-cols-2 gap-2 text-sm">
          <div className="text-muted-foreground">Purchase Price</div>
          <div className="text-right font-medium">
            {hasPurchaseCost ? (
              formatCurrency(purchasePrice)
            ) : (
              <span className="text-muted-foreground font-normal">N/A</span>
            )}
            {supplierName && (
              <span className="text-muted-foreground text-xs block">
                from {supplierName}
              </span>
            )}
          </div>

          <div className="text-muted-foreground">Sell Price</div>
          <div className="text-right font-medium">{formatCurrency(sellPrice)}</div>

          <div className="flex items-center gap-2 text-muted-foreground">
            <span>Venue Commission ({venueCommissionPercent}%)</span>
            <ToggleSwitch
              id="include-venue-commission"
              checked={includeVenueCommission}
              onCheckedChange={setIncludeVenueCommission}
              aria-label="Include venue commission in margin calculation"
              className="scale-90"
            />
          </div>
          <div className="text-right font-medium">
            {includeVenueCommission && result ? formatCurrency(result.commissionAmount) : '—'}
          </div>
        </div>

        <div className="border-t pt-3">
          {!hasPurchaseCost || !result ? (
            <p className="text-sm text-muted-foreground">
              Add a purchase price (unit cost) on the product to calculate margin. Margins are not
              shown when cost is unknown.
            </p>
          ) : (
          <div className="grid grid-cols-2 gap-2 text-sm">
            <div className="text-muted-foreground">
              {includeVenueCommission ? 'SKU margin (excl. commission)' : 'Margin'}
            </div>
            <div className="text-right">
              <MarginBadge
                marginPercent={result.grossMarginPercent}
                targetMargin={targetMarginPercent}
              />
            </div>

            {includeVenueCommission ? (
              <>
                <div className="text-muted-foreground">Operating margin (incl. commission)</div>
                <div className="text-right text-muted-foreground">
                  {result.operatingMarginPercent.toFixed(1)}%
                </div>
              </>
            ) : null}

            {includeVenueCommission ? (
              <>
                <div className="text-muted-foreground">Net Revenue</div>
                <div className="text-right font-medium">
                  {formatCurrency(result.netRevenue)}
                </div>
              </>
            ) : null}

            <div className="text-muted-foreground">
              {includeVenueCommission ? 'Gross Profit' : 'Profit'}
            </div>
            <div className="text-right font-medium">
              {formatCurrency(result.grossProfit)}
            </div>
          </div>
          )}
        </div>

        {result && !result.meetsTarget && result.suggestedSellPrice && (
          <div
            className={cn(
              'rounded-md border p-3 text-sm',
              ragStatus === 'red'
                ? 'bg-red-50 border-red-200'
                : 'bg-amber-50 border-amber-200'
            )}
          >
            <div className="flex items-start gap-2">
              <span className="text-lg">⚠️</span>
              <div>
                <div className="font-medium">Below target margin ({targetMarginPercent}%)</div>
                <div className="text-muted-foreground mt-1">
                  Suggested Price: <strong>{formatCurrency(result.suggestedSellPrice)}</strong>{' '}
                  to achieve {targetMarginPercent}% margin
                  {includeVenueCommission ? ' after venue commission' : ''}
                </div>
              </div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default MarginCalculator;
